import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * BusCctvModal — click any bus on the map, this opens as a full-screen
 * live-feed overlay. Plays the in-bus CCTV clip with YOLO person boxes
 * drawn in sync, and pipes the head-count into an occupancy chip that
 * drives the C_residual signal the MPC actually reads.
 *
 * Honesty: SIMULATED. In production this maps to the ONVIF/GB-T-28181
 * stream from the bus's DVR — the client is transport-agnostic.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MockPill } from "../common/MockPill";
import { getBaseUrl } from "../../lib/api";
const backdrop = {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 13, 16, 0.72)",
    zIndex: 3000,
    display: "grid",
    placeItems: "center",
    padding: 20,
    backdropFilter: "blur(4px)",
};
const modal = {
    width: "min(920px, 100%)",
    maxHeight: "min(720px, 92vh)",
    background: "var(--color-pure-white)",
    borderRadius: 6,
    overflow: "hidden",
    display: "grid",
    gridTemplateRows: "auto 1fr auto",
    boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
};
const header = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 18px",
    borderBottom: "1px solid var(--color-pebble)",
    gap: 12,
};
const body = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.6fr) minmax(240px, 1fr)",
    minHeight: 0,
};
const videoWrap = {
    position: "relative",
    background: "#000d10",
    minHeight: 0,
};
const overlay = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
};
const chip = {
    position: "absolute",
    top: 12,
    left: 12,
    padding: "8px 12px",
    background: "rgba(0, 13, 16, 0.78)",
    color: "#e8f6f0",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.06em",
    display: "flex",
    gap: 10,
    alignItems: "baseline",
};
const recDot = {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#e04a3a",
    boxShadow: "0 0 0 3px rgba(224, 74, 58, 0.28)",
    animation: "bb-rec-blink 1.4s ease-in-out infinite",
};
const timestamp = {
    position: "absolute",
    top: 12,
    right: 12,
    padding: "5px 9px",
    background: "rgba(0, 13, 16, 0.78)",
    color: "#e8f6f0",
    borderRadius: 4,
    fontSize: 11,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
};
const sidebar = {
    padding: "18px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    borderLeft: "1px solid var(--color-pebble)",
    overflowY: "auto",
    background: "var(--color-pure-white)",
};
const metric = {
    padding: "10px 12px",
    border: "1px solid var(--color-pebble)",
    borderRadius: 4,
};
const metricLabel = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "var(--color-cool-ash)",
    marginBottom: 4,
};
const metricValue = {
    fontSize: 22,
    fontWeight: 700,
    fontFeatureSettings: '"tnum"',
    lineHeight: 1.1,
};
const footer = {
    display: "flex",
    gap: 10,
    padding: "12px 18px",
    borderTop: "1px solid var(--color-pebble)",
    justifyContent: "flex-end",
    background: "var(--color-pure-white)",
};
const primaryBtn = {
    padding: "10px 16px",
    background: "#000d10",
    color: "#f6e6df",
    border: "1px solid #000d10",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    cursor: "pointer",
};
const ghostBtn = {
    padding: "10px 16px",
    background: "transparent",
    color: "var(--color-deep-ink, #000d10)",
    border: "1px solid var(--color-pebble)",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    cursor: "pointer",
};
function fmtTs(t) {
    const mm = Math.floor(t / 60);
    const ss = Math.floor(t % 60);
    return `${mm.toString().padStart(2, "0")}:${ss.toString().padStart(2, "0")}`;
}
/**
 * Distinct in-bus clips per vehicle so no two buses share footage.
 * Phase 16 · 2026-07-28 — swapped from stock CCTV clips to real BEST /
 * NMMT in-bus footage supplied by Sayuj. Hash the vehicle id → 0..N-1
 * → pick one of the four `bus-cam-real-N.mp4` files. The `.bboxes.json`
 * sibling is generated fresh by scripts/yolo_cctv.py against the real
 * footage — no stock frames leak into the pipeline.
 */
const CLIP_POOL = [
    "bus-cam-real-1.mp4",
    "bus-cam-real-2.mp4",
    "bus-cam-real-3.mp4",
    "bus-cam-real-4.mp4",
];
function hashToIndex(id, mod) {
    let h = 0;
    for (let i = 0; i < id.length; i++) {
        h = (h * 31 + id.charCodeAt(i)) >>> 0;
    }
    return h % mod;
}
function clipForVehicle(vehicleId) {
    const clip = CLIP_POOL[hashToIndex(vehicleId, CLIP_POOL.length)] ?? CLIP_POOL[0];
    const stem = clip.replace(/\.mp4$/, "");
    return {
        video: `/videos/${clip}`,
        bboxes: `/videos/${stem}.bboxes.json`,
    };
}
function findFrame(doc, t) {
    const f = doc.frames;
    if (f.length === 0)
        return null;
    let lo = 0, hi = f.length - 1;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if ((f[mid]?.t ?? 0) <= t)
            lo = mid;
        else
            hi = mid - 1;
    }
    return f[lo] ?? null;
}
const NOMINAL_CAPACITY = 40; // BEST midi bus seated + standing
export function BusCctvModal({ vehicle, onClose, onHold }) {
    const videoRef = useRef(null);
    const [doc, setDoc] = useState(null);
    const [time, setTime] = useState(0);
    const [peakCount, setPeakCount] = useState(0);
    const [detErr, setDetErr] = useState(null);
    const clip = useMemo(() => clipForVehicle(vehicle.vehicle.id), [vehicle.vehicle.id]);
    useEffect(() => {
        let cancelled = false;
        setDoc(null);
        setDetErr(null);
        setPeakCount(0);
        // Primary → fallback to the first real cam if the chosen variant's
        // .bboxes.json hasn't shipped for some reason.
        const primary = clip.bboxes;
        const fallback = "/videos/bus-cam-real-1.bboxes.json";
        fetch(primary)
            .then((r) => r.ok
            ? r.json()
            : fetch(fallback).then((r2) => r2.ok ? r2.json() : Promise.reject(new Error(String(r2.status)))))
            .then((d) => {
            if (!cancelled)
                setDoc(d);
        })
            .catch((e) => {
            if (!cancelled)
                setDetErr(String(e.message ?? e));
        });
        return () => {
            cancelled = true;
        };
    }, [clip.bboxes]);
    useEffect(() => {
        const v = videoRef.current;
        if (!v)
            return;
        const onTime = () => setTime(v.currentTime);
        v.addEventListener("timeupdate", onTime);
        return () => v.removeEventListener("timeupdate", onTime);
    }, []);
    const frame = useMemo(() => (doc ? findFrame(doc, time) : null), [doc, time]);
    const currentCount = frame?.count ?? 0;
    useEffect(() => {
        if (currentCount > peakCount)
            setPeakCount(currentCount);
    }, [currentCount, peakCount]);
    // Phase 12: push the live YOLO count to the backend signal registry so
    // Copilot answers about occupancy cite the SAME number the judge sees.
    // Throttle to at most once/second per vehicle.
    const lastPostedRef = useRef({ t: 0, count: -1 });
    useEffect(() => {
        if (!doc)
            return;
        const now = Date.now();
        if (now - lastPostedRef.current.t < 1000 &&
            currentCount === lastPostedRef.current.count) {
            return;
        }
        lastPostedRef.current = { t: now, count: currentCount };
        void fetch(`${getBaseUrl()}/api/cctv/occupancy`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                vehicle_id: vehicle.vehicle.id,
                onboard: currentCount,
                capacity: NOMINAL_CAPACITY,
                frame_time_s: time,
            }),
        }).catch(() => {
            /* transport error is fine — signal is best-effort */
        });
    }, [currentCount, time, doc, vehicle.vehicle.id]);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape")
                onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    const handleBackdrop = useCallback((e) => {
        if (e.target === e.currentTarget)
            onClose();
    }, [onClose]);
    const fillPct = Math.min(100, Math.round((currentCount / NOMINAL_CAPACITY) * 100));
    const cResidual = Math.max(0, NOMINAL_CAPACITY - currentCount);
    const strokeColor = fillPct > 80 ? "#bc7155" : "#4fa3c7";
    return createPortal(_jsxs("div", { style: backdrop, onClick: handleBackdrop, "data-testid": "bus-cctv-modal", children: [_jsxs("div", { style: modal, children: [_jsxs("div", { style: header, children: [_jsxs("div", { style: { minWidth: 0 }, children: [_jsxs("div", { style: {
                                            fontSize: 10,
                                            fontWeight: 700,
                                            letterSpacing: "0.16em",
                                            textTransform: "uppercase",
                                            color: "var(--color-cool-ash)",
                                            marginBottom: 4,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                        }, children: [_jsx("span", { children: "Live CCTV \u00B7 in-bus" }), _jsx(MockPill, { kind: "simulated" })] }), _jsxs("div", { style: {
                                            fontSize: 18,
                                            fontWeight: 700,
                                            letterSpacing: "-0.2px",
                                        }, children: [vehicle.vehicle.id, " \u00B7 Route ", vehicle.trip.route_id] })] }), _jsx("button", { type: "button", style: ghostBtn, onClick: onClose, "aria-label": "Close", "data-testid": "bus-cctv-close", children: "\u2715" })] }), _jsxs("div", { style: body, children: [_jsxs("div", { style: videoWrap, children: [_jsx("video", { ref: videoRef, autoPlay: true, muted: true, loop: true, playsInline: true, preload: "auto", style: {
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                        }, onError: () => {
                                            const v = videoRef.current;
                                            if (v && !v.src.endsWith("bus-cam-real-1.mp4")) {
                                                v.src = "/videos/bus-cam-real-1.mp4";
                                                v.play().catch(() => { });
                                            }
                                        }, children: _jsx("source", { src: clip.video, type: "video/mp4" }) }, clip.video), _jsx("svg", { style: overlay, viewBox: doc ? `0 0 ${doc.width} ${doc.height}` : "0 0 1280 720", preserveAspectRatio: "none", "aria-hidden": true, children: (frame?.boxes ?? []).map((b, i) => {
                                            const [x1, y1, x2, y2] = b;
                                            return (_jsxs("g", { children: [_jsx("rect", { x: x1, y: y1, width: Math.max(0, x2 - x1), height: Math.max(0, y2 - y1), fill: "none", stroke: strokeColor, strokeWidth: 2, vectorEffect: "non-scaling-stroke" }), _jsx("rect", { x: x1, y: Math.max(0, y1 - 22), width: 70, height: 20, fill: strokeColor, opacity: 0.9 }), _jsx("text", { x: x1 + 6, y: Math.max(14, y1 - 7), fill: "#ffffff", fontSize: 13, fontFamily: "ui-monospace, SFMono-Regular, monospace", fontWeight: 700, children: "person" })] }, i));
                                        }) }), _jsxs("div", { style: chip, children: [_jsx("span", { "aria-hidden": true, style: recDot }), _jsx("span", { style: { fontSize: 22, lineHeight: 1 }, children: currentCount }), _jsx("span", { style: {
                                                    fontSize: 10,
                                                    opacity: 0.85,
                                                    letterSpacing: "0.14em",
                                                    textTransform: "uppercase",
                                                }, children: "Onboard \u00B7 YOLOv8" })] }), _jsx("div", { style: timestamp, children: `CAM · ${fmtTs(time)}` })] }), _jsxs("div", { style: sidebar, children: [_jsxs("div", { style: metric, children: [_jsx("div", { style: metricLabel, children: "Fill" }), _jsxs("div", { style: metricValue, children: [fillPct, _jsx("span", { style: {
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            color: "var(--color-cool-ash)",
                                                            marginLeft: 4,
                                                        }, children: "%" })] }), _jsx("div", { style: {
                                                    marginTop: 6,
                                                    height: 6,
                                                    background: "var(--color-pebble)",
                                                    borderRadius: 999,
                                                    overflow: "hidden",
                                                }, children: _jsx("div", { style: {
                                                        width: `${fillPct}%`,
                                                        height: "100%",
                                                        background: strokeColor,
                                                        transition: "width 220ms ease",
                                                    } }) })] }), _jsxs("div", { style: metric, children: [_jsx("div", { style: metricLabel, children: "C_residual (seats left)" }), _jsx("div", { style: metricValue, children: cResidual }), _jsx("div", { style: { fontSize: 11, color: "var(--color-cool-ash)", marginTop: 4 }, children: "Feeds full-bus discount in MPC \u00B7 never hold a full bus." })] }), _jsxs("div", { style: metric, children: [_jsx("div", { style: metricLabel, children: "Peak this session" }), _jsx("div", { style: metricValue, children: peakCount })] }), _jsxs("div", { style: metric, children: [_jsx("div", { style: metricLabel, children: "Vehicle" }), _jsx("div", { style: { fontSize: 13, fontWeight: 600 }, children: vehicle.vehicle.id }), _jsxs("div", { style: { fontSize: 11, color: "var(--color-cool-ash)", marginTop: 2 }, children: ["Trip ", vehicle.trip.trip_id ?? "—", " \u00B7 stop seq", " ", vehicle.current_stop_sequence ?? "—"] })] }), detErr ? (_jsxs("div", { style: {
                                            fontSize: 11,
                                            padding: "8px 10px",
                                            background: "#f6e6df",
                                            border: "1px solid #e5c3b3",
                                            color: "#7a4a38",
                                            borderRadius: 4,
                                        }, children: ["YOLO bbox stream unavailable \u2014 showing zeroed count. Run", " ", _jsx("code", { children: "scripts/yolo_cctv.py" }), "."] })) : null] })] }), _jsxs("div", { style: footer, children: [_jsx("button", { type: "button", style: ghostBtn, onClick: onClose, children: "Close" }), onHold ? (_jsx("button", { type: "button", style: primaryBtn, onClick: () => onHold(vehicle), "data-testid": "bus-cctv-hold", children: "Hold this bus" })) : null] })] }), _jsx("style", { children: `
        @keyframes bb-rec-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
      ` })] }), document.body);
}
